import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Row, Col, Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useGetInventorySettings, useUpdateInventorySettings } from '../../api/inventory/service';
import { useGetStatuses } from 'api/inventory-status/service';
import Select from '../Select';
import { useGetChannels } from 'api/channels/service';
import { getActiveColorStyle } from 'utils/colorUtils';
import { getTheme2FormPageDarkSelectStyles } from 'utils/theme2MultiSelect';
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from 'utils/sidebarShellTheme';

const INSTITUTION_INVENTORY_TAB_STYLES = `
  body.white-content .institution-inventory-card__title {
    color: #1a1a1b;
  }
  body:not(.white-content) .institution-inventory-card__title {
    color: rgba(255, 255, 255, 0.88);
  }
  body:not(.white-content) .institution-inventory-card__description {
    color: rgba(255, 255, 255, 0.55) !important;
  }
`;

const InstitutionInventoryTab = ({
    permissions,
    activeColorStyle: activeColorStyleProp,
    activeColor = 'blue',
    isTheme2 = false,
    selectStyles: selectStylesProp,
}) => {
    const titleFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : '14px';
    const descriptionFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : '13px';
    const inputFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : '14px';
    const buttonFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : '12px';
    const activeColorStyle = useMemo(
        () => activeColorStyleProp ?? getActiveColorStyle(activeColor),
        [activeColorStyleProp, activeColor]
    );
    const selectStyles = useMemo(
        () =>
            selectStylesProp ??
            getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
        [selectStylesProp, activeColorStyle, isTheme2]
    );
    const { t } = useTranslation();
    const { data: inventorySettings, isLoading: inventorySettingsLoading } = useGetInventorySettings();
    const { mutate: updateInventorySettings } = useUpdateInventorySettings();
    const [form, setForm] = useState({
        stock_number_assignment_option: 'incrementing_number',
        stock_number_purchase_prefix: '',
        next_available_stock_number: '',
        stock_number_partial_vin: 'right',
        stock_number_partial_vin_number: 0,
        stock_number_rep_prefix: '',
        status_for_repo: null,
        status_for_inventory: null,
        default_location: null,
    });
    const { data: locationsData } = useGetChannels("sales_location_branch");
    const { data: statusesData, isLoading: isLoadingStatuses } = useGetStatuses();

    const statusOptions = useMemo(() => {
        const results = statusesData?.results || [];
        return results.map((s) => ({ label: s.status_name, value: s.id }));
    }, [statusesData]);

    const assignmentOptions = useMemo(() => [
        { value: 'incrementing_number', label: t('userDefinedInventoryTabs.incrementingNumber') },
        { value: 'partial_vin', label: t('userDefinedInventoryTabs.partialVin') }
    ], [t]);

    const partialVinOptions = useMemo(() => [
        { value: 'right', label: t('userDefinedInventoryTabs.right') },
        { value: 'left', label: t('userDefinedInventoryTabs.left') }
    ], [t]);

    const locationOptions = useMemo(() => {
        const results = locationsData?.results || [];
        return results
            .filter((location) => location?.type === 'sales_location_branch')
            .map((location) => ({
                value: location.id,
                label: location.name,
            }));
    }, [locationsData]);

    useEffect(() => {
        if (inventorySettings) {
            setForm(prev => ({
                ...prev,
                ...inventorySettings,
                status_for_repo: inventorySettings.status_for_repo?.id || inventorySettings.status_for_repo || null,
                status_for_inventory: inventorySettings.status_for_inventory?.id || inventorySettings.status_for_inventory || null,
                default_location:
                    inventorySettings.default_location?.id ||
                    inventorySettings.default_location ||
                    null,
            }));
        }
    }, [inventorySettings]);

    const handleUpdateInventorySettings = () => {
        updateInventorySettings({ id: inventorySettings.documentId, data: form });
    };

    const renderCard = (title, description, input) => (
        <Card style={{ overflow: 'hidden', borderRadius: 0 }} className="mb-2 pb-0 pt-0">
            <CardBody className="py-2 px-3">
                <Row className="align-items-center">
                    <Col md="8" className="d-flex flex-column justify-content-center">
                        <h4
                            className="mb-1 font-weight-bold institution-inventory-card__title"
                            style={{ fontSize: titleFontSize }}
                        >
                            {title}
                        </h4>
                        <div
                            className="text-muted institution-inventory-card__description"
                            style={{ fontSize: descriptionFontSize, lineHeight: '1.4' }}
                        >
                            {description}
                        </div>
                    </Col>
                    <Col md="4" className="d-flex justify-content-end align-items-center">
                        <div style={{ width: '250px' }}>
                            {input}
                        </div>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );

    if (inventorySettingsLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <div className="spinner-border" role="status" style={{ color: activeColorStyle.borderColor }}>
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{INSTITUTION_INVENTORY_TAB_STYLES}</style>
            <div>
            {/* Update button on top for mobile view only */}
            <div className="d-md-none mb-3">
                <button
                    className="w-100"
                    color="primary"
                    size="sm"
                    style={{
                        ...activeColorStyle,
                        borderRadius: 0,
                        fontSize: buttonFontSize,
                        padding: '8px 12px',
                        color: 'white',
                        border: 'none',
                    }}
                    onClick={handleUpdateInventorySettings}
                >
                    {t('userDefinedInventoryTabs.update')}
                </button>
            </div>

            {renderCard(
                t('userDefinedInventoryTabs.stockNumberAssignmentOption'),
                t('userDefinedInventoryTabs.stockNumberAssignmentDescription'),
                <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder="Select"
                    name="stock_number_assignment_option"
                    isSearchable={false}
                    closeMenuOnSelect={true}
                    value={assignmentOptions.find(opt => opt.value === form.stock_number_assignment_option) || null}
                    onChange={(option) => setForm({ ...form, stock_number_assignment_option: option?.value || '' })}
                    options={assignmentOptions}
                />
            )}

            {form.stock_number_assignment_option === 'partial_vin' && (
                <>
                    {renderCard(
                        t('userDefinedInventoryTabs.stockNumberPartialVin'),
                        t('userDefinedInventoryTabs.partialVinDescription'),
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            placeholder="Select"
                            name="stock_number_partial_vin"
                            isSearchable={false}
                            closeMenuOnSelect={true}
                            value={partialVinOptions.find(opt => opt.value === form.stock_number_partial_vin) || null}
                            onChange={(option) => setForm({ ...form, stock_number_partial_vin: option?.value || '' })}
                            options={partialVinOptions}
                        />
                    )}

                    {renderCard(
                        t('userDefinedInventoryTabs.stockNumberPartialVin'),
                        t('userDefinedInventoryTabs.partialVinNumberDescription'),
                        <Input
                            type="number"
                            min={1}
                            value={form.stock_number_partial_vin_number}
                            onChange={(e) => setForm({ ...form, stock_number_partial_vin_number: e.target.value })}
                            style={{ fontSize: inputFontSize, height: '38px' }}
                            className="mb-0 text-center"
                        />
                    )}
                </>
            )}

            {renderCard(
                t('userDefinedInventoryTabs.nextAvailableStockNumber'),
                t('userDefinedInventoryTabs.nextAvailableStockNumberDescription'),
                <Input
                    type="text"
                    value={form.next_available_stock_number}
                    onChange={(e) => setForm({ ...form, next_available_stock_number: e.target.value })}
                    style={{ fontSize: inputFontSize, height: '38px' }}
                    className="mb-0 text-center"
                />
            )}

            {renderCard(
                t('userDefinedInventoryTabs.stockNumberPurchasePrefix'),
                t('userDefinedInventoryTabs.stockNumberPurchasePrefixDescription'),
                <Input
                    type="text"
                    value={form.stock_number_purchase_prefix}
                    onChange={(e) => setForm({ ...form, stock_number_purchase_prefix: e.target.value })}
                    style={{ fontSize: inputFontSize, height: '38px' }}
                    className="mb-0 text-center"
                />
            )}

            {renderCard(
                t('userDefinedInventoryTabs.stockNumberRepoPrefix'),
                t('userDefinedInventoryTabs.stockNumberRepoPrefixDescription'),
                <Input
                    type="text"
                    value={form.stock_number_rep_prefix}
                    onChange={(e) => setForm({ ...form, stock_number_rep_prefix: e.target.value })}
                    style={{ fontSize: inputFontSize, height: '38px' }}
                    className="mb-0 text-center"
                />
            )}
            {renderCard(
                t('userDefinedInventoryTabs.defaultLocation'),
                t('userDefinedInventoryTabs.defaultLocationDescription'),
                <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder={t('searchInventory.selectLocation')}
                    name="default_location"
                    isSearchable
                    closeMenuOnSelect={true}
                    value={locationOptions.find(opt => opt.value === form.default_location) || null}
                    onChange={(option) => setForm({ ...form, default_location: option?.value || null })}
                    options={locationOptions}
                    isClearable
                />
            )}

            {renderCard(
                t('userDefinedInventoryTabs.defaultInventoryStatus'),
                t('userDefinedInventoryTabs.defaultInventoryStatusDescription'),
                <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder="Select Status"
                    name="status_for_inventory"
                    isSearchable={false}
                    closeMenuOnSelect={true}
                    value={statusOptions.find(opt => opt.value === form.status_for_inventory) || null}
                    onChange={(option) => setForm({ ...form, status_for_inventory: option?.value || null })}
                    options={statusOptions}
                    isDisabled={isLoadingStatuses}
                />
            )}

            {renderCard(
                t('userDefinedInventoryTabs.defaultRepoStatus'),
                t('userDefinedInventoryTabs.defaultRepoStatusDescription'),
                <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder="Select Status"
                    name="status_for_repo"
                    isSearchable={false}
                    closeMenuOnSelect={true}
                    value={statusOptions.find(opt => opt.value === form.status_for_repo) || null}
                    onChange={(option) => setForm({ ...form, status_for_repo: option?.value || null })}
                    options={statusOptions}
                    isDisabled={isLoadingStatuses}
                />
            )}


            {/* Update button on bottom for desktop view only */}
            <div className="d-none d-md-flex justify-content-end mt-3">
                <button
                    color="primary"
                    size="sm"
                    style={{
                        ...activeColorStyle,
                        borderRadius: 0,
                        fontSize: buttonFontSize,
                        padding: '6px 12px',
                        color: 'white',
                        border: 'none',
                    }}
                    onClick={handleUpdateInventorySettings}
                >
                    {t('userDefinedInventoryTabs.update')}
                </button>
            </div>
        </div>
        </>
    );
};

export default InstitutionInventoryTab;